import { useEffect, useRef, useState } from "react";
import { api, formatErr, canWrite, currentCompetenzaMese, CATEGORIE_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, FileText, Receipt } from "lucide-react";
import { toast } from "sonner";

const ACC = ".xlsx,.xls,.csv,.pdf";
const IMPORTS = [
  { kind: "dkv", title: "DKV (Gasolio)", icon: <FileSpreadsheet className="w-5 h-5" />, accept: ACC },
  { kind: "uta", title: "UTA (Gasolio)", icon: <FileSpreadsheet className="w-5 h-5" />, accept: ACC },
  { kind: "q8", title: "Q8 (Gasolio)", icon: <FileSpreadsheet className="w-5 h-5" />, accept: ACC },
  { kind: "telepass", title: "Telepass / Telepass DKV (anche PDF scansionati)", icon: <FileText className="w-5 h-5" />, accept: ACC },
  { kind: "estratto-conto", title: "Estratto conto banca", icon: <Receipt className="w-5 h-5" />, accept: ACC },
];

export default function Imports() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "import");
  const [azienda, setAzienda] = useState(company === "all" ? "golmar" : company);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(null);
  const [mese, setMese] = useState(currentCompetenzaMese());
  const [canone, setCanone] = useState("");
  const [banca, setBanca] = useState("__auto");
  const [banche, setBanche] = useState([]);
  useEffect(() => { api.get("/banche", { params: { company: azienda } }).then(r => setBanche(r.data)); }, [azienda, results]);

  const upload = async (kind, file) => {
    if (!file) return;
    setLoading(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company", azienda);
    if (mese) fd.append("mese", mese);
    if (kind === "dkv" && canone) fd.append("canone", canone);
    if (kind === "estratto-conto" && banca !== "__auto") fd.append("banca", banca);
    try {
      let { data } = await api.post(`/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.job_id) {
        toast.info("PDF scansionato: lettura automatica in corso (circa 1 minuto)…");
        for (let i = 0; i < 100; i++) {
          await new Promise((r) => setTimeout(r, 4000));
          let j;
          try { j = (await api.get(`/import/jobs/${data.job_id}`)).data; } catch (err) { if (err.response?.status === 404) throw err; continue; }
          if (j.stato === "errore") throw { response: { data: { detail: j.errore } } };
          if (j.stato === "completato") { data = j.risultato; break; }
        }
        if (data.job_id) throw new Error("Lettura ancora in corso: controlla Consumi importati tra qualche minuto");
      }
      setResults((prev) => ({ ...prev, [kind]: data }));
      toast.success(`${kind.toUpperCase()}: ${data.created} movimenti creati su ${data.parsed} righe`
        + (data.estera ? ` · fattura estera ${data.paese}: importata con il totale già ivato` : "")
        + (data.service_fee ? ` · Service fee UTA €${data.service_fee} (imponibile): aggiungila in Costi carte-dispositivi` : ""));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    setLoading(null);
  };

  const aggiornaExcel = async (pdf, xlsx) => {
    setLoading("excel");
    const fd = new FormData();
    fd.append("file", pdf); fd.append("excel", xlsx); fd.append("company", azienda);
    if (canone) fd.append("canone", canone);
    try {
      const r = await api.post("/import/dkv/excel", fd, { responseType: "blob" });
      const rep = JSON.parse(r.headers["x-report"] || "{}");
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = xlsx.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setResults((prev) => ({ ...prev, excel: rep }));
      toast.success(`Excel aggiornato: foglio ${rep.foglio}, colonna ${rep.colonna}`);
    } catch (e) {
      let msg = "Errore aggiornamento Excel";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch {}
      toast.error(msg);
    }
    setLoading(null);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-black">Import file</h1>
        <div className="text-sm text-muted-foreground">Carica fogli mensili e estratti conto. Gasolio (DKV, UTA, Q8) e Telepass vanno in "Tessere/Telepass → Consumi importati", da cui li riporti nei conteggi; gli estratti conto vanno in Prima nota.</div>
      </div>

      <Card className="p-4 rounded-md flex items-center gap-4">
        <div>
          <Label className="text-xs font-heading uppercase tracking-wider">Azienda destinazione</Label>
          <Select value={azienda} onValueChange={setAzienda}>
            <SelectTrigger className="h-9 w-56 mt-1" data-testid="import-company-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="golmar">Golmar</SelectItem>
              <SelectItem value="vitam">Vitam</SelectItem>
              <SelectItem value="rotta66">ROTTA66</SelectItem>
              <SelectItem value="fmg">Consorzio FMG</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-heading uppercase tracking-wider">Mese di competenza</Label>
          <div className="mt-1"><MonthPicker value={mese} onChange={setMese} className="h-9 w-44" testId="import-mese" /></div>
        </div>
        <div>
          <Label className="text-xs font-heading uppercase tracking-wider">Canone carte DKV (€)</Label>
          <Input type="number" step="0.01" placeholder="0,00" value={canone} onChange={(e) => setCanone(e.target.value)} className="h-9 w-36 mt-1" data-testid="import-canone" />
        </div>
        <div>
          <Label className="text-xs font-heading uppercase tracking-wider">Banca estratto conto</Label>
          <Select value={banca} onValueChange={setBanca}>
            <SelectTrigger className="h-9 w-48 mt-1" data-testid="import-banca-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto">Riconosci dal file</SelectItem>
              {banche.map((b) => <SelectItem key={b.id} value={b.abbreviazione}>{b.abbreviazione} · {b.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground max-w-md">Formati accettati: Excel (.xlsx/.xls), CSV e PDF (anche scansionati per Telepass). Il mese viene letto dal file (es. foglio "SETTEMBRE 26" o data della fattura DKV); se manca, si usa quello indicato qui. Le tessere DKV vengono abbinate anche se appartengono a un'altra azienda.</div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {IMPORTS.map((imp) => (
          <UploadCard key={imp.kind} {...imp} loading={loading === imp.kind} result={results[imp.kind]} disabled={!writable}
                      onFile={(f) => upload(imp.kind, f)} />
        ))}
      </div>
      <ExcelDkvCard loading={loading === "excel"} disabled={!writable} result={results.excel} onRun={aggiornaExcel} />
    </div>
  );
}

function ExcelDkvCard({ loading, disabled, result, onRun }) {
  const [pdf, setPdf] = useState(null);
  const [xlsx, setXlsx] = useState(null);
  return (
    <Card className="p-5 rounded-md border-2 border-dashed" data-testid="excel-dkv-card">
      <div className="font-heading font-bold">Aggiorna il file Excel DKV con la fattura PDF</div>
      <div className="text-xs text-muted-foreground mb-3">Scrive gli importi di ogni JOLLY nella colonna della quindicina (1° = E, 2° = I) del foglio del mese e le spese bancarie. Le formule restano invariate; un JOLLY nuovo viene aggiunto in una riga libera. Scarichi il file aggiornato.</div>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs font-heading uppercase">Fattura DKV (PDF)</Label>
          <Input type="file" accept=".pdf" className="h-9 mt-1 w-72" onChange={(e) => setPdf(e.target.files[0])} data-testid="excel-dkv-pdf" /></div>
        <div><Label className="text-xs font-heading uppercase">Excel DKV GASOLIO</Label>
          <Input type="file" accept=".xlsx" className="h-9 mt-1 w-72" onChange={(e) => setXlsx(e.target.files[0])} data-testid="excel-dkv-xlsx" /></div>
        <Button size="sm" className="h-9" disabled={!pdf || !xlsx || loading || disabled} onClick={() => onRun(pdf, xlsx)} data-testid="excel-dkv-run">
          {loading ? "Elaborazione…" : "Aggiorna e scarica Excel"}</Button>
      </div>
      {result && <div className="mt-3 text-sm bg-muted/40 p-2.5 rounded-sm font-mono" data-testid="excel-dkv-report">
        Foglio {result.foglio}{result.foglio_creato ? " (creato)" : ""} · colonna {result.colonna} · aggiornati {result.aggiornati}
        {result.aggiunti?.length > 0 && <div>Righe aggiunte: {result.aggiunti.join(", ")}</div>}
        {result.senza_posto?.length > 0 && <div className="text-destructive">Nessuna riga libera per: {result.senza_posto.join(", ")}</div>}
      </div>}
    </Card>
  );
}

function UploadCard({ kind, title, icon, accept, loading, result, onFile, disabled }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  return (
    <Card className="p-5 rounded-md border-2 border-dashed hover:border-primary/60 transition-colors"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) onFile(e.dataTransfer.files[0]); }}
          data-testid={`import-card-${kind}`}
          style={drag ? { borderColor: "hsl(var(--primary))", background: "hsl(var(--primary)/0.05)" } : {}}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-sm bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
        <div>
          <div className="font-heading font-bold">{title}</div>
          <div className="text-xs text-muted-foreground">Excel, CSV o PDF</div>
        </div>
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden"
             onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} data-testid={`import-input-${kind}`} />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={loading || disabled}
              className="w-full gap-2" data-testid={`import-button-${kind}`}>
        <Upload className="w-4 h-4" />
        {disabled ? "Non autorizzato" : loading ? "Caricamento…" : "Scegli file o trascina qui"}
      </Button>
      {result && (
        <div className="mt-3 text-sm bg-muted/40 p-2.5 rounded-sm font-mono">
          <div>Parsed: {result.parsed}</div>
          <div>Creati: {result.created}</div>
          {kind === "estratto-conto" && (result.banca
            ? <div data-testid="import-banca-result">Banca: <b>{result.banca}</b></div>
            : <div className="text-orange-600" data-testid="import-banca-result">Banca non riconosciuta: indicala nella colonna Banca della Prima nota</div>)}
          {kind === "estratto-conto" && result.da_regola > 0 && <div className="text-green-600" data-testid="import-da-regola">Assegnate in automatico dalle tue regole: {result.da_regola}</div>}
          {kind === "estratto-conto" && result.da_verificare > 0 && <div className="text-orange-600" data-testid="import-da-verificare">Da verificare in Prima nota (Coda eccezioni): {result.da_verificare}</div>}
          {kind === "estratto-conto" && result.categorie && <div className="text-xs text-muted-foreground mt-1" data-testid="import-categorie">
            {Object.entries(result.categorie).map(([k, n]) => `${CATEGORIE_LABELS[k] || k}: ${n}`).join(" · ")}</div>}
          {result.dove?.length > 0 && <div className="font-semibold" data-testid={`import-dove-${kind}`}>Trovi le righe in Consumi importati → {result.dove.map((d) => `${d.company.toUpperCase()} · ${new Date(`${d.mese}-01T12:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}`).join(", ")}</div>}
          {result.unmatched_tessere?.length > 0 && <div className="text-orange-600">Tessere non abbinate: {result.unmatched_tessere.join(", ")}</div>}
          {result.unmatched_targhe?.length > 0 && <div className="text-orange-600" data-testid={`import-unmatched-${kind}`}>Non abbinati (dispositivo/targa): {result.unmatched_targhe.join(", ")} — inserisci quel numero nella tessera Telepass DKV o la targa nel veicolo, poi assegnali da Consumi importati</div>}
          {result.pagine_saltate?.length > 0 && <div className="text-red-600" data-testid={`import-pagine-saltate-${kind}`}>Pagine non leggibili: {result.pagine_saltate.join(", ")} — controllale a mano</div>}
        </div>
      )}
    </Card>
  );
}
